/**
 * `useUpdateProjectFileStatus` mutation hook.
 *
 * Wraps the raw `updateProjectFileStatus` service fn.
 *
 * Cache invalidation:
 *   - `files.detail(projectId, fileId)` — the file's own status field.
 *   - `folders.files(projectId, folderId)` — patched in-place (not
 *     invalidated): the approved file's status is written into every cached
 *     folder-files list so the filmstrip/grid update without a refetch.
 *   - `folders.detail(projectId, folderId)` — the folder's own
 *     approvalProgress aggregation.
 *   - `folders.detail(projectId, ancestorId)` for each breadcrumb ancestor —
 *     approvalProgress aggregates the full subtree (recursive CTE on BE), so
 *     every ancestor's detail cache also goes stale. The folder is read from
 *     the TanStack cache to pull its breadcrumbs without re-fetching.
 *   - `folders.list(projectId, *)` prefix — every root + nested folder list
 *     page shows approvalProgress on each row and must refetch.
 *
 * Side effects: NONE. Toast/redirect side effects belong to the caller.
 */
import type {
  FolderResponseDto,
  ListFilesResponseDto,
  ProjectFileResponseDto,
  UpdateFileStatusDto,
} from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PROJECTS_QUERY_KEYS } from '@/entities/project';

import { updateProjectFileStatus } from './project-files.api';

export { updateProjectFileStatus };

interface UseUpdateProjectFileStatusOptions {
  projectId: string;
  fileId: string;
  /**
   * Optional. When provided, also invalidates folder-scoped caches so the
   * approval-progress aggregates refresh in any open folder table.
   */
  folderId?: string;
}

export function useUpdateProjectFileStatus(options: UseUpdateProjectFileStatusOptions) {
  const queryClient = useQueryClient();
  const { projectId, fileId, folderId } = options;

  return useMutation<ProjectFileResponseDto, Error, UpdateFileStatusDto['status']>({
    mutationFn: (status) => updateProjectFileStatus(projectId, fileId, { status }),
    onSuccess: (updatedFile) => {
      void queryClient.invalidateQueries({
        queryKey: PROJECTS_QUERY_KEYS.files.detail(projectId, fileId),
      });
      if (folderId) {
        queryClient.setQueriesData<ListFilesResponseDto>(
          { queryKey: PROJECTS_QUERY_KEYS.folders.files(projectId, folderId) },
          (cached) =>
            cached
              ? {
                  ...cached,
                  items: cached.items.map((item) =>
                    item.id === fileId ? { ...item, status: updatedFile.status } : item,
                  ),
                }
              : cached,
        );
        void queryClient.invalidateQueries({
          queryKey: PROJECTS_QUERY_KEYS.folders.detail(projectId, folderId),
        });
        void queryClient.invalidateQueries({
          queryKey: PROJECTS_QUERY_KEYS.folders.list(projectId).slice(0, 4),
        });

        // Walk ancestors via the cached folder detail. invalidateQueries above is
        // fire-and-forget — the cache entry is still present (just marked stale)
        // until the next fetch resolves, so reading it here is intentional and
        // correct. The `?.` guard handles the rare case where the entry was
        // garbage-collected since mount; ancestors won't be invalidated, but the
        // broad `folders.list` invalidation already covers list-level staleness
        // and the next folder-detail navigation will refetch fresh.
        const folder = queryClient.getQueryData<FolderResponseDto>(
          PROJECTS_QUERY_KEYS.folders.detail(projectId, folderId),
        );
        folder?.breadcrumbs.forEach((crumb) => {
          void queryClient.invalidateQueries({
            queryKey: PROJECTS_QUERY_KEYS.folders.detail(projectId, crumb.id),
          });
        });
      }
    },
  });
}
